import { addMonths, format, subMonths } from "date-fns";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { Calendar as ReactCalendar } from "react-calendar";
import { IntervalDay } from "../Steps/AppointmentStep/AppointmentStep";
import { CalendarArrow } from "./CalendarArrow/CalendarArrow";
import { nb } from "date-fns/locale";
import { GtmService } from "../GtmService";
import { useSwipeable } from "react-swipeable";
import classNames from "classnames";

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];
type CalendarTileProps = {
  date: Date;
  view: string;
};

type StyledCalendarProps = {
  selectedDay: Date;
  onChange: (day: Date) => void;
  interval?: IntervalDay[];
  selectedMonth: Date;
  onMonthChange: (month: Date) => void;
  loading?: boolean;
  disabled?: boolean;
  setSelectedDay: any;
  noAppointmentsTodayLabel?: string;
  firstAvailableDateButton?: string;
  firstAvailableDate: Date | null;
};

export const Calendar: FC<StyledCalendarProps> = ({
  selectedDay,
  onChange,
  interval = [],
  selectedMonth,
  onMonthChange,
  loading = false,
  disabled = false,
  setSelectedDay,
  noAppointmentsTodayLabel,
  firstAvailableDateButton,
  firstAvailableDate,
}) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const defaultFirstAvailableDateButton = "Hopp til neste ledige dato ($date)";
  const defaultNoAppointmentsTodayLabel = "Ingen ledige timer i dag";

  const handleDateChange = (newValue: Value) => {
    if (newValue instanceof Date) {
      onChange(newValue);
    }
  };

  const dateFormat = "EEEE d. MMMM";

  const handleActiveStartDateChange = ({
    action,
  }: {
    action: "prev" | "next";
  }) => {
    if (action === "prev") {
      onMonthChange(subMonths(selectedMonth, 1));
    }
    if (action === "next") {
      onMonthChange(addMonths(selectedMonth, 1));
    }
    return;
  };

  const hasAppointments = (date: Date): boolean => {
    if (interval.length === 0) return false;

    const intervalDay = interval.find(
      (day) => day.date.toDateString() === date.toDateString()
    );

    return intervalDay?.hasAppointments ?? false;
  };

  useEffect(() => {
    if (!loading && !hasAppointments(selectedDay)) {
      GtmService.noAppointmentsToday();
    }
  }, [selectedDay, hasAppointments, loading]);

  useEffect(() => {
    GtmService.appointmentDateSelected(selectedDay);
  }, [selectedDay]);

  const firstAvailableDateButtonText = (
    firstAvailableDateButton
      ? firstAvailableDateButton
      : defaultFirstAvailableDateButton
  ).replace(
    "$date",
    firstAvailableDate
      ? format(firstAvailableDate, dateFormat, { locale: nb })
      : "ingen dato"
  );

  const handlers = useSwipeable({
    onSwiping: () => {
      setIsSwiping(true);
    },
    onSwiped: () => {
      setIsSwiping(false);
    },
    onSwipedLeft: () => {
      onMonthChange(addMonths(selectedMonth, 1));
    },
    onSwipedRight: () => {
      const today = new Date();
      const prevMonth = subMonths(selectedMonth, 1);

      if (prevMonth < new Date(today.getFullYear(), today.getMonth(), 1)) {
        return;
      }
      onMonthChange(prevMonth);
    },
  });

  return (
    <div
      className="calendar-container"
      role="group"
      aria-label="Kalender for valg av dato"
      aria-busy={loading}
    >
      {!loading && (
        <div
          {...handlers}
          className={classNames("calendar-swipe-wrapper", {
            "calendar-swipe-wrapper--swiping": isSwiping,
          })}
        >
          <ReactCalendar
            onChange={handleDateChange}
            value={hasAppointments(selectedDay) ? selectedDay : null}
            locale="nb-NO"
            activeStartDate={selectedMonth}
            onActiveStartDateChange={handleActiveStartDateChange}
            formatDay={(locale: string, date: Date) =>
              date.getDate().toString()
            }
            formatShortWeekday={(locale: string, date: Date) => {
              const days = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];
              return days[date.getDay()];
            }}
            showNeighboringMonth={true}
            next2Label={null}
            prev2Label={null}
            nextLabel={
              <CalendarArrow direction="next" ariaLabel="Neste måned" />
            }
            prevLabel={
              <CalendarArrow direction="prev" ariaLabel="Forrige måned" />
            }
            tileDisabled={({ date, view }: CalendarTileProps) => {
              if (view === "month" && !loading && !disabled) {
                return !hasAppointments(date);
              }
              return loading || disabled;
            }}
            tileClassName={({ date, view }: CalendarTileProps) => {
              if (view === "month") {
                if (hasAppointments(date)) {
                  return "tile-has-appointment";
                } else {
                  return "tile-no-appointment";
                }
              }
              return "";
            }}
            minDate={new Date()}
            minDetail="month"
            maxDetail="month"
          />
        </div>
      )}
      {firstAvailableDateButtonText && (
        <div className="no-appointments">
          <h2>{format(selectedDay, dateFormat, { locale: nb })}</h2>
          {!hasAppointments(selectedDay) && (
            <>
              <p>
                {noAppointmentsTodayLabel
                  ? noAppointmentsTodayLabel
                  : defaultNoAppointmentsTodayLabel}
              </p>
              <button
                onClick={() => {
                  GtmService.firstAvailableDateSelected();
                  const dateToSelect = firstAvailableDate ?? new Date();
                  setSelectedDay(dateToSelect);
                  onMonthChange(
                    new Date(
                      dateToSelect.getFullYear(),
                      dateToSelect.getMonth(),
                      1
                    )
                  );
                }}
              >
                {firstAvailableDateButtonText}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
